import React from 'react';
import { Box, Modal, Typography } from '@mui/material';
import Hero1Img from '../assets/Hero1.png';
import Hero2Img from '../assets/Hero2.png';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { ClickableIcon } from '@client/new/components/basic';
import { PrimaryButton } from '@client/new/components/button';
import { deprecatedClassnames } from '@client/new/helpers/cssHelpers';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '688px',
  bgcolor: 'background.paper',
  border: 'none',
  borderRadius: '10px',
  p: 4,
  outline: 'none',
};

type Option = {
  value: string;
  label: string;
  image?: string;
  video?: string;
};

const options = [
  { value: 'HeroFullImage', label: 'Hero image', image: Hero2Img },
  {
    value: 'HeroFullVideo',
    label: 'Hero video',
    video: import.meta.env.BASE_URL + 'static/images/builder/VideoHero.mp4',
  },
  {
    value: 'Introduction',
    label: 'Hero with benefits list',
    image: Hero1Img,
  },
] as const satisfies readonly Option[];

export type HeroSectionType = (typeof options)[number]['value'];

type HeroOption = Option & { value: HeroSectionType };

type HeroSelectProps = {
  onHeroSelect: (o: HeroSectionType) => void;
  onClose: () => void;
};

export const HeroSelectModal = ({ onHeroSelect, onClose }: HeroSelectProps) => {
  const [selectedOption, onSelectOption] = React.useState<null | HeroOption>(
    null,
  );

  const handleSelect = (o: HeroOption | null) => {
    if (!o) return;

    onHeroSelect(o.value);
    onClose();
  };

  // https://mui.com/material-ui/react-modal/
  return (
    <Modal
      open={true}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="relative">
          <ClickableIcon
            warn
            icon={faClose}
            className="text-2xl absolute right-0 top-[-10px]"
            onClick={onClose}
            title="Close"
          />
          <Typography
            id="modal-modal-title"
            variant="h4"
            component="h4"
            textAlign="center"
          >
            Select hero type
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            <section className="flex flex-wrap gap-6">
              {options.map((o: HeroOption) => (
                <div key={o.value}>
                  <div
                    className={deprecatedClassnames(
                      'h-[200px] w-[300px] flex items-center',
                      o.label === selectedOption?.label
                        ? 'border border-primary rounded-lg'
                        : '',
                    )}
                  >
                    {o.video ? (
                      <video
                        src={o.video}
                        className="cursor-pointer max-h-[200px]"
                        onClick={() => onSelectOption(o)}
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : (
                      <img
                        className={deprecatedClassnames(
                          'cursor-pointer max-h-[200px] w-full h-[168.75px]',
                        )}
                        src={o.image}
                        onClick={() => onSelectOption(o)}
                        alt={o.label}
                      />
                    )}
                  </div>

                  <Typography
                    variant="h5"
                    component="div"
                    className={deprecatedClassnames(
                      'mt-2',
                      o.label === selectedOption?.label ? 'text-primary' : '',
                    )}
                  >
                    {o.label}
                  </Typography>
                </div>
              ))}

              <PrimaryButton
                disabled={selectedOption === null}
                className="w-full mt-5 h-10 text-lg"
                onClick={() => handleSelect(selectedOption)}
              >
                Insert
              </PrimaryButton>
            </section>
          </Typography>
        </div>
      </Box>
    </Modal>
  );
};
